import { omit, pathOr } from 'ramda';
import qs, { StringifiableRecord } from 'query-string';
import { API_PREFIX } from '../constants/EndpointTemplate';
import { HttpMethods } from './http';
import SettingsManager from './SettingsManager';
import NetworkError from './NetworkError';
import { fetch, AbortController } from './http';

export interface SDKRequestInit extends RequestInit {
  query?: StringifiableRecord;
  baseUrl?: string;
  signal?: AbortSignal | null;
  apiVersion?: string | undefined;
  productType?: string | undefined;
}

type BodyExtendedRequestInit = Omit<RequestInit, 'body'> & {
  body?: BodyInit | Record<string, any> | null;
};

type RawFetchConfig = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

class ApiFetch {
  constructor(private readonly settings: SettingsManager) {}

  public static processHeaders(headers: HeadersInit): Record<string, string> {
    if (headers instanceof Headers) {
      return Object.fromEntries(headers.entries());
    }

    if (Array.isArray(headers)) {
      return Object.fromEntries(headers);
    }

    return headers;
  }

  private getUrlWithQuery(url: string, query: StringifiableRecord = {}) {
    return qs.stringifyUrl({ query, url });
  }

  get<T>(url: string, config?: SDKRequestInit) {
    return this.fetch<T>(this.getUrlWithQuery(url, config?.query), {
      ...omit(['query'], config),
      method: HttpMethods.GET,
    });
  }

  bodyModifier(data?: any): BodyInit {
    return JSON.stringify(data);
  }

  post<T>(url: string, config?: BodyExtendedRequestInit) {
    const requestInit: RequestInit = {
      ...config,
      body: config?.body ? this.bodyModifier(config?.body) : undefined,
      method: HttpMethods.POST,
    };

    return this.fetch<T>(url, requestInit);
  }

  put<T>(url: string, config?: BodyExtendedRequestInit) {
    const requestInit: RequestInit = {
      ...config,
      method: HttpMethods.PUT,
      body: config?.body ? this.bodyModifier(config?.body) : undefined,
    };

    return this.fetch<T>(url, requestInit);
  }

  delete<T>(url: string, config?: RequestInit) {
    const requestInit: RequestInit = {
      ...config,
      method: HttpMethods.DELETE,
    };
    if (config?.body) {
      requestInit.body = JSON.stringify(config?.body);
    }

    return this.fetch<T>(url, requestInit);
  }

  public fetch<T>(url: string, config?: SDKRequestInit) {
    try {
      const abortMethodName = this.settings.getAbortMethodName();
      const controller = new AbortController();

      const requestConfig = { ...omit(['signal'], config) };
      const signal = abortMethodName ? controller.signal : config?.signal;
      const abortSignal =
        signal && signal instanceof AbortSignal ? { signal } : {};

      const request = this.request<T>(url, {
        ...requestConfig,
        ...abortSignal,
      });

      if (abortMethodName) {
        // @ts-ignore
        request[abortMethodName] = () => {
          try {
            controller.abort();
          } catch (e) {
            console.log('Eva-Fe-Sdk aborting error', e);
          }
        };
      }

      return request;
    } catch (e) {
      return Promise.reject('Request was canceled');
    }
  }

  private urlModifier(
    url: string,
    apiVersion?: string,
    productType?: string
  ): string {
    let resultUrl = url;

    if (apiVersion) {
      resultUrl = url.replace(API_PREFIX, `${API_PREFIX}${apiVersion}/`);
    }

    if (productType) {
      resultUrl = resultUrl.replace(
        this.settings.getProductType(),
        productType
      );
    }

    return resultUrl;
  }

  private request<T>(url: string, config?: SDKRequestInit) {
    const configHeaders = ApiFetch.processHeaders(
      pathOr({}, ['headers'], config)
    );
    const baseUrl = config?.baseUrl ?? this.settings.getBaseUrl();

    const modifiedUrl = this.urlModifier(
      url,
      config?.apiVersion,
      config?.productType
    );

    const requestUrl = this.getUrlWithQuery(modifiedUrl, config?.query);

    const requestConfig: RawFetchConfig = {
      ...omit(['query'], config),
      headers: {
        'Content-Type': 'application/json',
        ...configHeaders,
      },
    };

    return fetch(`${baseUrl}${requestUrl}`, requestConfig)
      .catch(err => {
        // INFO: fix specific unstable error:
        // TypeError Failed to execute 'fetch' on 'Window': Failed to read the 'signal'
        // property from 'RequestInit': Failed to convert value to 'AbortSignal'.
        if (
          err &&
          (err?.name?.includes?.('AbortError') ||
            err?.message?.includes?.('signal') ||
            err?.message?.includes?.('AbortSignal'))
        ) {
          console.warn(err.name, err?.message, err?.stack);
          // make another request w/o signal
          return fetch(`${baseUrl}${requestUrl}`, omit(['signal'], config));
        }

        return err;
      })
      .then((response: Response) => {
        if (!response?.text) {
          return Promise.reject(
            new NetworkError(response.status, response.statusText)
          );
        }

        return response.text().then(text => {
          const data = text ? JSON.parse(text) : {};
          if (!response.ok) {
            return Promise.reject(new NetworkError(response.status, data.code));
          }

          return data as T;
        });
      });
  }
}

export default ApiFetch;
